import React, { useContext, useMemo, useState } from 'react';
import { Product, Team } from 'teams';

import type { ComponentContext } from './rum-component-context';
import { RumComponentContext } from './rum-component-context';

export const useRumContext = () => useContext(RumComponentContext);

/**
 * Context Provider to add a new component to the action breadcrumbs. Useful for class Components.
 */
export const RumComponentContextProvider: React.FunctionComponent<
  React.PropsWithChildren<{
    componentName: string;
    teamName: Team;
    productName: Product;
    customAttributes?: object;
  }>
> = ({ componentName, teamName, productName, customAttributes, children }) => {
  const parentContext = useRumContext();
  const [team, setTeamName] = useState(teamName);
  const [product, setProductName] = useState(productName);
  const newContext = useMemo<ComponentContext>(
    () => ({
      component: componentName,
      teamName: team,
      productName: product,
      setTeamName,
      setProductName,
      customAttributes: {
        ...parentContext.customAttributes,
        ...customAttributes,
      },
      componentBreadCrumbs: `${parentContext.componentBreadCrumbs}.${componentName}`,
    }),
    [
      componentName,
      team,
      product,
      parentContext.componentBreadCrumbs,
      parentContext.customAttributes,
      customAttributes,
    ],
  );
  return <RumComponentContext.Provider value={newContext}>{children}</RumComponentContext.Provider>;
};
/**
 * Decorator to add a new component to the breadcrumbs when using useRumAction or useRumTracking action hooks
 * the decorator is better than a just component because it will add the context to everything in your component
 */
export function WithRumComponentContext<PropsType>(
  componentName: string,
  teamName: Team,
  productName: Product,
  options:
    | {
        customAttributes?: object;
      }
    | undefined,
  Component: React.FunctionComponent<React.PropsWithChildren<PropsType>>,
): React.FunctionComponent<React.PropsWithChildren<PropsType>>;
export function WithRumComponentContext<PropsType>(
  componentName: string,
  teamName: Team,
  productName: Product,
  Component: React.FunctionComponent<React.PropsWithChildren<PropsType>>,
): React.FunctionComponent<React.PropsWithChildren<PropsType>>;
export function WithRumComponentContext<PropsType extends JSX.IntrinsicAttributes>(
  componentName: string,
  teamName: Team,
  productName: Product,
  options: any,
  Component?: React.FunctionComponent<React.PropsWithChildren<PropsType>>,
): React.FunctionComponent<React.PropsWithChildren<PropsType>> {
  if (typeof Component === 'undefined') {
    return WithRumComponentContext(componentName, teamName, productName, {}, options);
  }
  return (props: PropsType) => {
    return (
      <RumComponentContextProvider
        componentName={componentName}
        teamName={teamName}
        productName={productName}
        customAttributes={options.customAttributes}
      >
        <Component {...props} />
      </RumComponentContextProvider>
    );
  };
}
